import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import {
  BaseComponentWithContextProps,
  componentWithContext,
} from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import Table from '../../../../../components/Table'
import IPInput from '../../../../../components/IPInput'
import IPSelectBox from '../../../../../components/IPSelectBox'
import type { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import {
  IOrderDetail,
  PreOrderAddProductRequest,
  PreOrderListModel,
  IGetMyOrdersResponse,
  IGetOrderDetailsResponse,
} from '../models/models'
import { useSearchParams } from 'react-router-dom'
import { DeletePreOrderProductApi } from '../services/deletePreOrderProductApi'
import { GetPreOrderApi } from '../services/getPreOrderApi'
import { SavePreOrderService } from '../services/SavePreOrderService'
import { CancelOrderApi } from '../services/cancelOrderApi'
import { AddProductPreOrderApi } from '../services/addProductPreOrderApi'
import { CreatePreOrderService } from '../services/CreatePreOrderService'
import IPDatePicker from '../../../../../components/IPDatePicker'
import { GetShippingDateApi } from '../services/getShippingDate'
import { useCustomReducer } from '../../../../../base/customHooks'
import Modal from '../../../../../components/IPModal/IPModal'

// ✅ Bayi onayına gönder servisi
import { SendDealerApprovalApi } from '../services/sendDealerApprovalApi'

interface PreOrderState {
  orderDetails: IOrderDetail
  showOrderDetails: boolean
  errorMessage: []
}

function EditPreOrder(props: BaseComponentWithContextProps) {
  const [searchParams] = useSearchParams()

  const [list, setList] = useState<PreOrderListModel[]>([])
  const [originalList, setOriginalList] = useState<PreOrderListModel[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [headerInfo, setHeaderInfo] = useState<Partial<PreOrderListModel>>({})
  const [originalHeaderInfo, setOriginalHeaderInfo] =
    useState<Partial<PreOrderListModel>>({})

  const [matchedProductIds, setMatchedProductIds] = useState<number[]>([])
  const [selectedCurrency, setSelectedCurrency] = useState<string>('TRY')
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('1801')
  const [cancelled, setCancelled] = useState<boolean>(false)

  const [newProductCode, setNewProductCode] = useState<string>('')
  const [newProductCount, setNewProductCount] = useState<string>('1')

  // Sevkiyat
  const [shipmentDate, setShipmentDate] = useState<Date | null>(null)
  const [shippingTypes, setShippingTypes] = useState<ISelectOption[]>([])
  const [selectedShippingType, setSelectedShippingType] = useState<string>('')
  const [selectedShippingName, setSelectedShippingName] = useState<string>('')

  // Sipariş No Detay Modal
  const [showOrderNoModal, setShowOrderNoModal] = useState<boolean>(false)
  const [selectedOrderNo, setSelectedOrderNo] = useState<string>('')
  const [selectedOrderRows, setSelectedOrderRows] = useState<PreOrderListModel[]>([])

  const getInitialState = (): PreOrderState => {
    return {
      orderDetails: {
        DealerName: '',
        Address: '',
        City: '',
        District: '',
        OrderNumber: '',
        Currency: 0,
        CreatedDate: new Date(),
        ShipmentDate: new Date(),
        ShipmentType: '',
        OrderReturnCompanyList: [
          {
            Cargo: '',
            CargoValue: 0,
            Items: [
              {
                Warehouse: '',
                ProductCode: '',
                ProductName: '',
                Quantity: 0,
                Currency: 0,
                Price1: '',
                Price1Value: 0,
                Price2: '',
                Price2Value: 0,
                TotalPrice1: '',
                TotalPrice1Value: 0,
                TotalPrice2: '',
                TotalPrice2Value: 0,
              },
            ],
            Company: '',
            OrderNumber: '',
            PaymentMethod: '',
            CurrencyType: '',
            CurrencyValue: '',
            MT: '',
            TotalPrice: '',
            TotalKdv: '',
            Total: '',
          },
        ],
        Total: 0,
        ErrorMessages: [],
        IsStockError: [],
      },
      showOrderDetails: false,
      errorMessage: [],
    }
  }

  const intialState = getInitialState()
  const [state, dispatchState] = useCustomReducer<PreOrderState>(intialState)

  // ===== Helpers =====
  const hasOrderNo = (row: PreOrderListModel) =>
    (row.SaporderNumber?.trim()?.length ?? 0) > 0

  const isCancelledStatus = (row: PreOrderListModel) => row.Status === -1

  const isAutoMatched = (row: PreOrderListModel) =>
    (row.ProductName || '').trim().toLowerCase() ===
    (row.ProductNameFromDB || '').trim().toLowerCase()

  const isVerified = (row: PreOrderListModel) =>
    isAutoMatched(row) || matchedProductIds.includes(row.Id)

  // DealerApproval:
  // 0: Yönetici Onayı Bekliyor
  // 1: Onay Bekliyor (onaya gönderilmiş)
  // 2: Onaylandı
  // -1: Reddedildi
  const getDealerApproval = (row: PreOrderListModel): number | undefined => {
    const v = (row as any)?.DealerApproval
    return typeof v === 'number' ? v : undefined
  }

  const isDealerApprovalPendingRow = (row: PreOrderListModel) => getDealerApproval(row) === 1
  const isDealerApprovedFinalRow = (row: PreOrderListModel) => getDealerApproval(row) === 2

  const isDealerApprovalSentOrFinalRow = (row: PreOrderListModel) =>
    isDealerApprovalPendingRow(row) || isDealerApprovedFinalRow(row)

  const getDealerApprovalLabel = (row: PreOrderListModel): string => {
    const a = getDealerApproval(row)
    if (a === 0) return 'Onayınız Bekliyor'
    if (a === 1) return 'Bayi Onayı Bekliyor'
    if (a === -1) return 'Reddedildi'
    if (a === 2) return 'Onaylandı'
    return 'Bilinmiyor'
  }

  const formatDate = (date: Date) => {
    const d = new Date(date)
    const year = '' + d.getFullYear()
    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day
    return [year, month, day].join('-')
  }

  const stripTimeFromSapDate = (s?: string) => {
    if (!s) return ''
    return s.split(' ')[0] || s
  }

  const currencySymbolFromCode = (code?: string) => {
    const c = (code || '').toUpperCase().trim()
    if (c === 'TRY' || c === 'TL') return '₺'
    if (c === 'USD') return '$'
    if (c === 'EUR') return '€'
    return c || ''
  }

  const currencySymbolFromRow = (row?: PreOrderListModel) => {
    const raw = (row?.Currency || '').trim()
    if (raw === '₺' || raw === '$' || raw === '€') return raw
    return currencySymbolFromCode(raw)
  }

  const getStatusLabel = (status?: number): string => {
    switch (status) {
      case 1:
        return 'Oluşturuldu'
      case 2:
        return 'Sevk edildi'
      case -1:
        return 'İptal edildi'
      case 0:
      default:
        return 'Bekliyor'
    }
  }

  const hasUnsavedChanges = () => {
    const listChanged = JSON.stringify(list) !== JSON.stringify(originalList)
    const headerChanged =
      JSON.stringify(headerInfo) !== JSON.stringify(originalHeaderInfo)
    return listChanged || headerChanged
  }

  const formatMoney2 = (value: any) => {
    const n = Number(value)
    if (Number.isNaN(n)) return null
    return n.toFixed(2)
  }

  /**
   * KİLİT KURALI:
   * - TÜM ürünlerin sipariş numarası doluysa -> düzenleme kilit
   * - sadece "Yeni Ürün Ekle" açık kalır
   */
  const allProductsHaveOrderNumber = useMemo(() => {
    if (list.length === 0) return false
    return list.every(x => hasOrderNo(x))
  }, [list])

  const isEditLocked = allProductsHaveOrderNumber

  // ✅ İPTAL EDİLMİŞ ÜRÜN KURALI:
  // - iptal edilmiş ürün için hiçbir işlem yapılamasın
  const isRowTotallyLocked = (row: PreOrderListModel) => isCancelledStatus(row)

  // ✅ SATIR KİLİDİ:
  // - İptal edilmişse tam kilit
  // - Sipariş numarası varsa kilit
  // - Onaya gönderilmiş(1) veya onaylanmış(2) ise adet/fiyat/doğrulama/silme dahil kilit
  const isRowEditLocked = (row: PreOrderListModel) =>
    isRowTotallyLocked(row) || hasOrderNo(row) || isDealerApprovalSentOrFinalRow(row)

  // ✅ Fiyat + Silme kilidi:
  // - İptal edilmişse kilit
  // - (1) ve (2) için kilit
  const isPriceAndDeleteLocked = (row: PreOrderListModel) =>
    isRowTotallyLocked(row) || isDealerApprovalSentOrFinalRow(row)

  // ===== Load =====
  useEffect(() => {
    const referenceNumber = searchParams.get('refNumber')

    if (!referenceNumber) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Referans numarası bulunamadı',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    props.showLoading()
    const api = new GetPreOrderApi({})
    api
      .getPreOrder(referenceNumber)
      .then((data: PreOrderListModel[]) => {
        props.hideLoading()
        setList(data)
        setOriginalList(JSON.parse(JSON.stringify(data)))

        if (data.length > 0) {
          setHeaderInfo(data[0])
          setOriginalHeaderInfo(JSON.parse(JSON.stringify(data[0])))
        }

        const autoMatchedIds = data.filter(isAutoMatched).map(x => x.Id)
        setMatchedProductIds(autoMatchedIds)
      })
      .catch(err => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err?.description || 'Ön sipariş verileri yüklenirken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }, [searchParams, cancelled])

  // ===== Header updates =====
  const handleHeaderChange = (field: keyof PreOrderListModel, value: string) => {
    if (isEditLocked) return

    setHeaderInfo(prev => ({
      ...prev,
      [field]: value,
    }))

    // ✅ Header değişiklikleri:
    // - iptal edilmiş satıra uygulanmasın
    // - onaya gönderilmiş(1) ve onaylanmış(2) satıra uygulanmasın
    setList(prev =>
      prev.map(item => {
        if (isRowTotallyLocked(item)) return item
        if (isDealerApprovalSentOrFinalRow(item)) return item
        return {
          ...item,
          [field]: value,
        }
      }),
    )
  }

  // ===== Row updates =====
  const toggleSelect = (id: number) => {
    const row = list.find(x => x.Id === id)
    if (!row) return
    if (isRowTotallyLocked(row)) return

    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )
  }

  const handleCountChange = (id: number, value: string) => {
    const item = list.find(x => x.Id === id)
    if (!item) return
    if (isEditLocked) return
    if (isRowEditLocked(item)) return

    const numeric = Number(value)
    if (Number.isNaN(numeric) || numeric < 1) return

    setList(prev => prev.map(row => (row.Id === id ? { ...row, Count: numeric } : row)))
  }

  const handlePriceChange = (id: number, value: string) => {
    const item = list.find(x => x.Id === id)
    if (!item) return
    if (isEditLocked) return
    if (isPriceAndDeleteLocked(item)) return
    if (isRowEditLocked(item)) return

    const numeric = Number(value)
    if (Number.isNaN(numeric) || numeric < 0) return

    setList(prev =>
      prev.map(row => (row.Id === id ? { ...row, PriceFromB2b: numeric } : row)),
    )
  }

  const handleProductMatchToggle = async (id: number, desiredChecked: boolean) => {
    const item = list.find(x => x.Id === id)
    if (!item) return
    if (isEditLocked) return
    if (isRowEditLocked(item)) return
    if (isAutoMatched(item)) return

    const result = await Swal.fire({
      icon: 'question',
      title: 'Ürün doğrulama',
      text: desiredChecked
        ? 'Exceldeki ürün adını, sistemdeki ürün adı ile eşleştirmek istediğinize emin misiniz?'
        : 'Ürün doğrulama eşleşmesini kaldırmak istediğinize emin misiniz?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })

    if (!result.isConfirmed) return

    if (desiredChecked) {
      setMatchedProductIds(prev => (prev.includes(id) ? prev : [...prev, id]))
      setList(prev =>
        prev.map(row => (row.Id === id ? { ...row, ProductName: row.ProductNameFromDB } : row)),
      )
    } else {
      setMatchedProductIds(prev => prev.filter(x => x !== id))
    }
  }

  // Sipariş No Modal aç
  const openOrderNoModal = (orderNo: string) => {
    const clean = (orderNo || '').trim()
    if (!clean) return
    const rows = list.filter(x => (x.SaporderNumber || '').trim() === clean)
    setSelectedOrderNo(clean)
    setSelectedOrderRows(rows)
    setShowOrderNoModal(true)
  }

  // ===== Delete product =====
  const deleteProductId = async (id: number, product: string) => {
    const item = list.find(x => x.Id === id)
    if (!item) return
    if (isEditLocked) return
    if (isRowTotallyLocked(item)) return
    if (isPriceAndDeleteLocked(item)) return
    if (isRowEditLocked(item)) return

    Swal.fire({
      icon: 'question',
      title: `${product} ürününü silmek istediğinize emin misiniz?`,
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(async result => {
      if (!result.isConfirmed) return

      props.showLoading()
      const api = new DeletePreOrderProductApi({})
      api
        .deleteProduct(id)
        .then((res: any) => {
          props.hideLoading()
          setCancelled(!cancelled)
          Swal.fire({
            icon: 'success',
            title: res,
            showConfirmButton: true,
            allowOutsideClick: false,
          })
        })
        .catch(err => {
          props.hideLoading()
          Swal.fire({
            icon: 'error',
            title: err?.description || 'Bir hata oluştu',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
        })
    })
  }

  // ===== Shipping date =====
  const fetchShippingDate = async (date: Date) => {
    props.showLoading()
    const api = new GetShippingDateApi({})

    const address = (headerInfo.Address || '').trim()
    const postCode = address.length >= 5 ? address.slice(-5) : address

    const request: any = {
      PostCode: postCode,
      ShippingDate: formatDate(date),
    }

    try {
      const result = await api.getShippingDate(request)
      props.hideLoading()

      if (!result) return

      if (!result.ShippingTypes || result.ShippingTypes.length === 0) {
        setShippingTypes([])
        setSelectedShippingType('')
        setSelectedShippingName('')
        Swal.fire({
          icon: 'error',
          title:
            'Bu tarihte seçmiş olduğunuz adrese gönderim yoktur. Lütfen yeni tarih seçiniz!!!',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        return
      }

      const options: ISelectOption[] = result.ShippingTypes.map((x: any) => ({
        value: x.ShippingType,
        label: x.ShippingName,
      }))

      setShippingTypes(options)

      const serverDate = result.ShippingDate ? new Date(result.ShippingDate) : date
      setShipmentDate(serverDate)

      const first = options[0]
      setSelectedShippingType(String(first.value))
      setSelectedShippingName(first.label)
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err?.description || err?.message || 'Sevkiyat tarihi alınırken hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // ===== ✅ Force Save (Price changed accepted) =====
  // Bayi onayına göndermeden önce, hataya takılmıyorsa "kaydet" servisini çalıştırır.
  // Fiyat değişmiş gibi kabul: PriceFromB2b alanı her satır için gönderilir.
  const savePreOrderForcePrices = async (): Promise<boolean> => {
    if (isEditLocked) return false

    if (!headerInfo.ReferenceNumber) {
      Swal.fire({
        icon: 'warning',
        title: 'Referans numarası boş olamaz',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }

    if (list.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'En az bir ürün olmalıdır',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }

    const api = new SavePreOrderService({})

    try {
      const res: any = await api.savePreOrder({
        ReferenceNumber: headerInfo.ReferenceNumber || '',
        CompanyName: headerInfo.CompanyName || '',
        DealerCode: headerInfo.DealerCode || '',
        DealerName: headerInfo.DealerName || '',
        Mail: headerInfo.Mail || '',
        TaxOffice: headerInfo.TaxOffice || '',
        TaxNumber: headerInfo.TaxNumber || '',
        PhoneNumber: headerInfo.PhoneNumber || '',
        TakerName: headerInfo.TakerName || '',
        TakerSurname: headerInfo.TakerSurname || '',
        City: headerInfo.City || '',
        District: headerInfo.District || '',
        Address: headerInfo.Address || '',
        Manager: headerInfo.Manager || '',
        ManagerMail: headerInfo.ManagerMail || '',
        SelectedCurrency: selectedCurrency,
        Products: list.map(item => ({
          Id: item.Id,
          ProductId: item.ProductId,
          ProductCode: item.ProductCode,
          ProductName: item.ProductName,
          ProductNameFromDB: item.ProductNameFromDB || '',
          Count: item.Count,
          Currency: item.Currency || '',
          Status: item.Status,
          OrderCreated: item.OrderCreated,
          // ✅ Zorla gönder: fiyat değişmiş gibi kabul
          PriceFromB2b: item.PriceFromB2b ?? 0,
        })),
      })

      // Save başarılı: local "original" snapshot'ı güncelle (ek bir bloklamaya gerek kalmasın)
      setOriginalList(JSON.parse(JSON.stringify(list)))
      setOriginalHeaderInfo(JSON.parse(JSON.stringify(headerInfo)))

      // İsterseniz burada toast göstermeyin; akışın sonunda başarı mesajı verilecek.
      // Ancak servis metni döndürüyorsa sessiz geçiyoruz.
      void res

      return true
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description || err?.message || 'Ön sipariş kaydedilirken bir hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }
  }

  // ===== Create order =====
  // ✅ YENİ KURAL: Onaylanmamış ürünler için sipariş oluşturma çalışmamalı (DealerApproval===2 olmalı)
  // ✅ Ayrıca iptal edilmiş ürünler hiçbir işleme dahil edilmesin
  const handleCreateSelected = async () => {
    if (isEditLocked) return

    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const cancelledSelected = selectedRows.filter(isRowTotallyLocked)
    if (cancelledSelected.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilmiş ürün seçili',
        text: 'İptal edilmiş ürünler için işlem yapılamaz. Lütfen seçimden kaldırınız.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const alreadyOrderedSelected = selectedRows.filter(x => hasOrderNo(x))

    const notApprovedSelected = selectedRows.filter(
      x => !hasOrderNo(x) && !isDealerApprovedFinalRow(x),
    )

    const creatableSelected = selectedRows.filter(
      x => !hasOrderNo(x) && isDealerApprovedFinalRow(x),
    )

    if (alreadyOrderedSelected.length > 0) {
      const firstNames = alreadyOrderedSelected
        .slice(0, 5)
        .map(x => x.ProductName)
        .join(', ')
      const extra =
        alreadyOrderedSelected.length > 5
          ? ` (+${alreadyOrderedSelected.length - 5} ürün)`
          : ''

      await Swal.fire({
        icon: 'warning',
        title: 'Seçili ürünlerin bir kısmı zaten sipariş edilmiş',
        html: `<div>Bu ürünler tekrar siparişe gönderilmez.<br/><br/><b>Örnek:</b> ${firstNames}${extra}</div>`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    if (notApprovedSelected.length > 0) {
      const firstNames = notApprovedSelected
        .slice(0, 5)
        .map(x => x.ProductName)
        .join(', ')
      const extra =
        notApprovedSelected.length > 5 ? ` (+${notApprovedSelected.length - 5} ürün)` : ''

      await Swal.fire({
        icon: 'warning',
        title: 'Onaylanmamış ürünler siparişe gönderilemez',
        html: `<div>Sipariş oluşturmak için ürünleri <b>bayinin onaylamış</b> olması gerekir.<br/><br/><b>Örnek:</b> ${firstNames}${extra}</div>`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    if (creatableSelected.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sipariş oluşturulacak ürün bulunamadı',
        text: 'Seçtiğiniz ürünler içinde siparişe uygun (siparişsiz ve onaylı) ürün yok.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const unverified = creatableSelected.filter(row => {
      if (isAutoMatched(row)) return false
      return !matchedProductIds.includes(row.Id)
    })
    if (unverified.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Doğrulanmamış Ürünler',
        text: 'Lütfen tüm seçili ürünleri doğrulayınız (Ürün Doğrulama)',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (hasUnsavedChanges()) {
      Swal.fire({
        icon: 'warning',
        title: 'Kaydedilmemiş Değişiklikler',
        text: 'Kaydetmediğiniz değişiklikler silinecektir. Önce kaydediniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (!shipmentDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Sevkiyat tarihi seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (!selectedShippingType) {
      Swal.fire({
        icon: 'warning',
        title: 'Gönderim formatı seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili ürünler için sipariş oluşturulsun mu?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    props.showLoading()
    const api = new CreatePreOrderService({})
    api
      .createPreOrder({
        ReferenceNumber: headerInfo.ReferenceNumber || '',
        CompanyName: headerInfo.CompanyName || '',
        DealerCode: headerInfo.DealerCode || '',
        DealerName: headerInfo.DealerName || '',
        Mail: headerInfo.Mail || '',
        TaxOffice: headerInfo.TaxOffice || '',
        TaxNumber: headerInfo.TaxNumber || '',
        PhoneNumber: headerInfo.PhoneNumber || '',
        TakerName: headerInfo.TakerName || '',
        TakerSurname: headerInfo.TakerSurname || '',
        City: headerInfo.City || '',
        District: headerInfo.District || '',
        Address: headerInfo.Address || '',
        Manager: headerInfo.Manager || '',
        ManagerMail: headerInfo.ManagerMail || '',
        SelectedCurrency: selectedCurrency,
        Warehouse: selectedWarehouse,
        ShippingType: selectedShippingType,
        ShippingName: shippingTypes.find(x => x.value === selectedShippingType)?.label || '',
        ShippingDate: formatDate(shipmentDate),
        Products: creatableSelected.map(item => ({
          Id: item.Id,
          ProductId: item.ProductId,
          ProductCode: item.ProductCode,
          ProductName: item.ProductName,
          ProductNameFromDB: item.ProductNameFromDB || '',
          Count: item.Count,
          Currency: item.Currency || '',
          Status: item.Status,
          OrderCreated: item.OrderCreated,
          PriceFromB2b: item.PriceFromB2b ?? 0,
        })),
      })
      .then((res: any) => {
        props.hideLoading()
        setCancelled(!cancelled)
        setSelectedIds([])
        finishOrder(res)
      })
      .catch(err => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err?.description || 'Ön sipariş oluşturulurken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  const finishOrder = (res: any) => {
    if (typeof res === 'string') {
      Swal.fire({
        icon: 'error',
        title: res,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (res.IsStockError && res.IsStockError.length > 0) {
      let msg = `<div>Sipariş Oluşturulmadı!<br />Aşağıdaki ürünler için stoğumuz tükenmiştir.<br/>`
      for (const orderNumber of res.IsStockError) {
        if (!orderNumber.StockAvaible) {
          msg += `<br /><span>${orderNumber.OutOfStock}</span>`
        }
      }
      msg += `<br/>Siparişinize devam edebilmek için sepetinizdeki tükenen ürünleri kaldırmanız gerekmektedir.</div>`

      Swal.fire({
        icon: 'warning',
        title: msg,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (res.ErrorMessages && res.ErrorMessages.length > 0) {
      if (!res.OrderReturnCompanyList || res.OrderReturnCompanyList.length === 0) {
        let msg = `<div>Sipariş Oluşturulmadı!`
        for (const orderNumber of res.ErrorMessages) {
          msg += `<br /><span>${orderNumber}</span>`
        }
        msg += `</div>`

        Swal.fire({
          icon: 'warning',
          title: msg,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        return
      }

      dispatchState({
        showOrderDetails: true,
        orderDetails: res,
        errorMessage: res.ErrorMessages,
      })
      return
    }

    dispatchState({ showOrderDetails: true, orderDetails: res })
  }

  // ===== Cancel order =====
  const handleCancelSelected = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const cancelledSelected = selectedRows.filter(isRowTotallyLocked)
    if (cancelledSelected.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilmiş ürün seçili',
        text: 'İptal edilmiş ürünler için işlem yapılamaz. Lütfen seçimden kaldırınız.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const invalidItems = list.filter(x => selectedIds.includes(x.Id) && !hasOrderNo(x))
    if (invalidItems.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilemeyen ürün(ler)',
        text: 'Sipariş numarası olmayan ürünler için sipariş iptal edilemez.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili ürünler iptal edilsin mi?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    props.showLoading()
    const api = new CancelOrderApi({})
    api
      .cancelOrder(selectedIds)
      .then((res: any) => {
        props.hideLoading()
        setCancelled(!cancelled)
        setSelectedIds([])
        Swal.fire({
          icon: 'success',
          title: res,
          timer: 1500,
          showConfirmButton: false,
        })
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err?.description || 'Sipariş iptal edilirken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  // ===== ✅ Bayi Onayına Gönder (ID list) =====
  // ✅ Yeni Akış:
  // 1) Validasyonlar
  // 2) Force Save (PriceFromB2b her satırda gönder)
  // 3) SendDealerApprovalApi
  const handleSendToDealerApprovalSelected = async () => {
    if (isEditLocked) return

    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const cancelledSelected = selectedRows.filter(isRowTotallyLocked)
    if (cancelledSelected.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilmiş ürün seçili',
        text: 'İptal edilmiş ürünler için işlem yapılamaz. Lütfen seçimden kaldırınız.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const alreadySentOrFinalSelected = selectedRows.filter(isDealerApprovalSentOrFinalRow)
    const sendableSelected = selectedRows.filter(x => !isDealerApprovalSentOrFinalRow(x))

    if (alreadySentOrFinalSelected.length > 0) {
      const firstNames = alreadySentOrFinalSelected
        .slice(0, 5)
        .map(x => x.ProductName)
        .join(', ')
      const extra =
        alreadySentOrFinalSelected.length > 5
          ? ` (+${alreadySentOrFinalSelected.length - 5} ürün)`
          : ''

      await Swal.fire({
        icon: 'warning',
        title: 'Seçili ürünlerin bir kısmı zaten onay sürecinde / onaylı',
        html: `<div>Onaya gönderilmiş veya onaylanmış ürünler tekrar onaya gönderilmez.<br/><br/><b>Örnek:</b> ${firstNames}${extra}</div>`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    if (sendableSelected.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Onaya gönderilemedi',
        text: 'Seçtiğiniz ürünlerin tamamı zaten onaya gönderilmiş veya onaylanmış.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const unverified = sendableSelected.filter(row => {
      if (isAutoMatched(row)) return false
      return !matchedProductIds.includes(row.Id)
    })
    if (unverified.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Doğrulanmamış Ürünler',
        text: 'Lütfen tüm seçili ürünleri doğrulayınız (Ürün Doğrulama)',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili ürünler bayi onayına gönderilsin mi?',
      html: `<div>Devam ederseniz sistem önce ön siparişi kaydedecek, ardından bayi onayına gönderecektir.</div>`,
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    const idList = sendableSelected.map(x => x.Id)

    props.showLoading()

    // 1) Force Save
    const saved = await savePreOrderForcePrices()
    if (!saved) {
      props.hideLoading()
      return
    }

    // 2) Send to approval
    const api = new SendDealerApprovalApi({})
    try {
      const res: any = await api.send(idList)

      props.hideLoading()
      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: res || 'Seçili ürünler bayi onayına gönderildi',
        showConfirmButton: true,
        allowOutsideClick: false,
      })

      setCancelled(prev => !prev)
      setSelectedIds([])
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description || err?.message || 'Bayi onayına gönderme sırasında hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // ===== Add product (always enabled) =====
  const handleAddProduct = async () => {
    if (hasUnsavedChanges()) {
      Swal.fire({
        icon: 'warning',
        title: 'Kaydedilmemiş Değişiklikler',
        text: 'Kaydetmediğiniz değişiklikler silinecektir. Önce kaydediniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const productCode = newProductCode.trim()
    const countNumber = Number(newProductCount)

    if (!productCode) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen ürün kodu giriniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (Number.isNaN(countNumber) || countNumber < 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen geçerli bir adet giriniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const req: PreOrderAddProductRequest = {
      RefNumber: headerInfo.ReferenceNumber ?? '',
      ProductCode: productCode,
      Count: countNumber,
    }

    props.showLoading()
    const api = new AddProductPreOrderApi({})
    api
      .addProduct(req)
      .then((res: any) => {
        setCancelled(!cancelled)
        props.hideLoading()
        Swal.fire({
          icon: 'success',
          title: res,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
      .catch(err => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: err?.description || 'Ürün eklenirken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })

    setNewProductCode('')
    setNewProductCount('1')
  }

  // ===== Save (locked when all have order no) =====
  const handleSavePreOrder = async () => {
    if (isEditLocked) return

    if (!headerInfo.ReferenceNumber) {
      Swal.fire({
        icon: 'warning',
        title: 'Referans numarası boş olamaz',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (list.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'En az bir ürün olmalıdır',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Ön sipariş kaydedilsin mi?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    props.showLoading()
    const api = new SavePreOrderService({})
    api
      .savePreOrder({
        ReferenceNumber: headerInfo.ReferenceNumber || '',
        CompanyName: headerInfo.CompanyName || '',
        DealerCode: headerInfo.DealerCode || '',
        DealerName: headerInfo.DealerName || '',
        Mail: headerInfo.Mail || '',
        TaxOffice: headerInfo.TaxOffice || '',
        TaxNumber: headerInfo.TaxNumber || '',
        PhoneNumber: headerInfo.PhoneNumber || '',
        TakerName: headerInfo.TakerName || '',
        TakerSurname: headerInfo.TakerSurname || '',
        City: headerInfo.City || '',
        District: headerInfo.District || '',
        Address: headerInfo.Address || '',
        Manager: headerInfo.Manager || '',
        ManagerMail: headerInfo.ManagerMail || '',
        SelectedCurrency: selectedCurrency,
        Products: list.map(item => {
          const original = originalList.find(o => o.Id === item.Id)

          const payload: any = {
            Id: item.Id,
            ProductId: item.ProductId,
            ProductCode: item.ProductCode,
            ProductName: item.ProductName,
            ProductNameFromDB: item.ProductNameFromDB || '',
            Count: item.Count,
            Currency: item.Currency || '',
            Status: item.Status,
            OrderCreated: item.OrderCreated,
          }

          const originalPrice = original?.PriceFromB2b ?? null
          const currentPrice = item.PriceFromB2b ?? null
          const isPriceChanged = original ? originalPrice !== currentPrice : currentPrice !== null
          if (isPriceChanged) payload.PriceFromB2b = item.PriceFromB2b

          return payload
        }),
      })
      .then((res: any) => {
        props.hideLoading()
        setCancelled(!cancelled)
        Swal.fire({
          icon: 'success',
          title: 'Başarılı',
          text: res || 'Ön sipariş başarıyla kaydedildi',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: err?.description || 'Ön sipariş kaydedilirken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  // ===== Table =====
  const tableColumns = useMemo(
    () => [
      { Header: '#', accessor: 'rowNo' },
      { Header: 'Ürün (Excel)', accessor: 'productName' },
      { Header: 'Ürün (Sistem)', accessor: 'productNameFromDb' },
      { Header: 'Ürün Doğrulama', accessor: 'productMatch' },
      { Header: 'Ürün Kodu', accessor: 'productCode' },
      { Header: 'Adet', accessor: 'count' },
      { Header: 'Fiyat', accessor: 'price' },
      { Header: 'Sipariş Durumu', accessor: 'orderStatus' },
      { Header: 'Sipariş No', accessor: 'sapOrderNumber' },
      { Header: 'Onay Durumu', accessor: 'approvalStatus' },
      { Header: 'Seç', accessor: 'select' },
      { Header: 'İşlemler', accessor: 'actions' },
    ],
    [],
  )

  const tableData = useMemo(
    () =>
      list.map((item, index) => {
        const auto = isAutoMatched(item)
        const checked = isVerified(item)
        const ordered = hasOrderNo(item)

        const totallyLocked = isRowTotallyLocked(item)
        const rowEditLocked = isEditLocked || isRowEditLocked(item)
        const rowPriceDeleteLocked = isEditLocked || isPriceAndDeleteLocked(item) || ordered

        const orderNoClickable = ordered && !isCancelledStatus(item)

        return {
          rowNo: index + 1,
          productName: item.ProductName,
          productNameFromDb: item.ProductNameFromDB,
          orderStatus:
            item.StatusText?.length > 0 && item.Status !== -1
              ? item.StatusText
              : getStatusLabel(item.Status),
          sapOrderNumber: (
            <span
              role={orderNoClickable ? 'button' : undefined}
              onClick={() => {
                if (!orderNoClickable) return
                openOrderNoModal(item.SaporderNumber)
              }}
              title={
                orderNoClickable
                  ? 'Sipariş detayını görüntüle'
                  : item.Status === -1
                    ? 'İptal siparişler tıklanamaz'
                    : 'Sipariş numarası yok'
              }
              style={{
                cursor: orderNoClickable ? 'pointer' : 'not-allowed',
                color: orderNoClickable ? '#0d6efd' : '#6c757d',
                textDecoration: orderNoClickable ? 'underline' : 'none',
                fontWeight: 600,
                display: 'inline-block',
              }}
            >
              {item.SaporderNumber || '-'}
            </span>
          ),
          productMatch: (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <input
                type='checkbox'
                checked={checked}
                disabled={totallyLocked || rowEditLocked || auto}
                onChange={e => handleProductMatchToggle(item.Id, e.target.checked)}
              />
            </div>
          ),
          productCode: item.ProductCode,
          count: (
            <IPInput
              type='number'
              value={item.Count}
              min={1}
              disabled={totallyLocked || rowEditLocked}
              onChange={e => handleCountChange(item.Id, e.target.value)}
            />
          ),
          price: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <IPInput
                type='number'
                step='0.01'
                min='0'
                value={item.PriceFromB2b ?? ''}
                disabled={totallyLocked || rowPriceDeleteLocked || rowEditLocked}
                onChange={e => {
                  const numeric = Number(e.target.value)
                  if (!Number.isNaN(numeric) && numeric >= 0) {
                    handlePriceChange(item.Id, e.target.value)
                  }
                }}
                style={{ flex: 1, minWidth: '100px' }}
              />
              <span style={{ whiteSpace: 'nowrap', minWidth: '40px', textAlign: 'center' }}>
                {currencySymbolFromRow(item)}
              </span>
            </div>
          ),
          approvalStatus: (
            <span
              className={
                getDealerApproval(item) === 2
                  ? 'badge badge-success'
                  : getDealerApproval(item) === -1
                    ? 'badge badge-warning'
                    : getDealerApproval(item) === 0
                      ? 'badge badge-warning'
                      : getDealerApproval(item) === 1
                        ? 'badge badge-info'
                        : 'badge badge-secondary'
              }
            >
              {getDealerApprovalLabel(item)}
            </span>
          ),
          select: (
            <input
              type='checkbox'
              checked={selectedIds.includes(item.Id)}
              onChange={() => toggleSelect(item.Id)}
              disabled={totallyLocked}
              title={totallyLocked ? 'İptal edilmiş ürün seçilemez' : 'Seç'}
            />
          ),
          actions: (
            <>
              {!totallyLocked && !ordered && !rowPriceDeleteLocked && !rowEditLocked && (
                <a
                  data-action-type='delete'
                  role='button'
                  style={{ cursor: 'pointer' }}
                  data-toggle='tooltip'
                  data-original-title={'Sil'}
                  onClick={() => deleteProductId(item.Id, item.ProductName)}
                >
                  <i className='icon-trash-2' style={{ fontSize: '1.2rem', color: 'red' }} />
                </a>
              )}
            </>
          ),
        }
      }),
    [list, selectedIds, matchedProductIds, isEditLocked],
  )

  const refNo = headerInfo.ReferenceNumber || list[0]?.ReferenceNumber

  // ===== Sipariş No Modal =====
  const modalRow: PreOrderListModel | null = useMemo(() => {
    return selectedOrderRows.length > 0 ? selectedOrderRows[0] : null
  }, [selectedOrderRows])

  const modalOrderHeader = useMemo(() => {
    const od = (modalRow?.OrderDetail?.[0] as IGetMyOrdersResponse | undefined) || undefined
    const lines = (modalRow?.OrderDetail2 || []) as IGetOrderDetailsResponse[]

    const currencyCode =
      (od?.DOVIZ_KODU || lines?.[0]?.SIPARIS_PARA_BIRIMI || '').toString()
    const currencySymbol = currencySymbolFromCode(currencyCode)

    const statusText =
      (modalRow?.StatusText && modalRow.StatusText.trim().length > 0
        ? modalRow.StatusText
        : getStatusLabel(modalRow?.Status)) || ''

    const orderDate = stripTimeFromSapDate(od?.SIPARIS_TARIHI)
    const paymentText = (od?.ODEME_BICIMI || '').trim()

    const shipmentInfoSet = new Set<string>()
    if (lines && lines.length > 0) {
      for (const l of lines) {
        const label = (l.SEVK_ACIKLAMA || '').trim()
        const date = stripTimeFromSapDate((l as any).SEVK_TARIHI)
        const txt = [label, date].filter(Boolean).join(' - ').trim()
        if (txt) shipmentInfoSet.add(txt)
      }
    }
    if (shipmentInfoSet.size === 0) {
      const firstSevk = (od?.SEVK_ACIKLAMA || '').trim()
      if (firstSevk) shipmentInfoSet.add(firstSevk)
    }
    const shipmentInfo = Array.from(shipmentInfoSet).join(' | ')

    return { statusText, orderDate, paymentText, currencySymbol, shipmentInfo }
  }, [modalRow])

  const orderLinesForModal = useMemo(() => {
    const lines = (modalRow?.OrderDetail2 || []) as IGetOrderDetailsResponse[]
    return lines.map(l => {
      const sym = currencySymbolFromCode(l.SIPARIS_PARA_BIRIMI)

      const lineKdv =
        typeof (l as any).SPB_TOPLAM_KDV === 'number'
          ? (l as any).SPB_TOPLAM_KDV
          : typeof (l as any).SPB_KDV === 'number'
            ? (l as any).SPB_KDV
            : typeof (l as any).KDV_TUTARI === 'number'
              ? (l as any).KDV_TUTARI
              : null

      return {
        URUN_KODU: l.URUN_KODU,
        URUN_ACIKLAMA: l.URUN_ACIKLAMA,
        TOPLAM_ADET: l.TOPLAM_ADET,
        BIRIM_TUTAR: l.BIRIM_TUTAR,
        TOPLAM_TUTAR: l.TOPLAM_TUTAR,
        PARA: sym,
        KDV: lineKdv,
      }
    })
  }, [modalRow])

  const modalTotals = useMemo(() => {
    const sym = modalOrderHeader.currencySymbol || ''
    const totalNet = orderLinesForModal.reduce((acc, l) => acc + (Number(l.TOPLAM_TUTAR) || 0), 0)
    const totalKdv = orderLinesForModal.reduce((acc, l) => acc + (Number(l.KDV) || 0), 0)
    const grandTotal = totalNet + totalKdv
    return { totalNet, totalKdv, grandTotal, sym }
  }, [orderLinesForModal, modalOrderHeader.currencySymbol])

  // ✅ Header buton disable:
  // - sadece hiç seçim yoksa veya genel edit lock varsa disable
  const isSendToDealerApprovalDisabled = useMemo(() => {
    if (isEditLocked) return true
    if (selectedIds.length === 0) return true
    return false
  }, [isEditLocked, selectedIds])

  return (
    <div>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <span className='h4'>
              Ön Sipariş Düzenle | <span style={{ color: 'red' }}>{refNo}</span>
            </span>

            <div className='col-md-5 d-flex align-items-center justify-content-end gap-2'>
              <IPButton
                text='Bayi Onayına Gönder'
                onClick={handleSendToDealerApprovalSelected}
                disabled={isSendToDealerApprovalDisabled}
              />
              <IPButton text='Kaydet' onClick={handleSavePreOrder} disabled={isEditLocked} />
            </div>
          </div>

          <div className='card-body'>
            <div className='mb-4'>
              <div className='row align-items-end'>
                <div className='col-12 col-md-4 col-lg-3'>
                  <div className='d-flex gap-2'>
                    <IPButton
                      text='Sipariş Oluştur'
                      onClick={handleCreateSelected}
                      disabled={isEditLocked}
                      style={{ flex: 1 }}
                    />
                    <IPButton
                      text='Sipariş İptal Et'
                      onClick={handleCancelSelected}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className='col-12 col-md-3 col-lg-1'>
                  <label className='mb-2 d-block'>Döviz</label>
                  <IPSelectBox
                    placeholder='Seçiniz'
                    options={[
                      { value: 'TRY', label: 'TRY' },
                      { value: 'USD', label: 'USD' },
                      { value: 'EUR', label: 'EUR' },
                    ]}
                    isDisabled={isEditLocked}
                    isClearable={false}
                    isSearchable={false}
                    value={{ value: selectedCurrency, label: selectedCurrency }}
                    onChangeSingle={(option: ISelectOption) =>
                      setSelectedCurrency(option.value as string)
                    }
                  />
                </div>

                <div className='col-12 col-md-3 col-lg-1'>
                  <label className='mb-2 d-block'>Sevk Tarihi</label>
                  <IPDatePicker
                    selected={shipmentDate as any}
                    onChange={(date: Date) => {
                      setShipmentDate(date)
                      fetchShippingDate(date)
                    }}
                    dateFormat={'dd-MM-yyyy'}
                    minDate={new Date()}
                    disabled={isEditLocked}
                  />
                </div>

                <div className='col-12 col-md-3 col-lg-3'>
                  <label className='mb-2 d-block'>Gönderim Formatı</label>
                  <IPSelectBox
                    placeholder='Seçiniz'
                    options={shippingTypes}
                    isDisabled={isEditLocked || shippingTypes.length === 0}
                    isClearable={false}
                    isSearchable={false}
                    value={
                      selectedShippingType
                        ? {
                          value: selectedShippingType,
                          label: selectedShippingName || selectedShippingType,
                        }
                        : undefined
                    }
                    onChangeSingle={(option: ISelectOption) => {
                      setSelectedShippingType(String(option.value))
                      setSelectedShippingName(option.label)
                    }}
                  />
                </div>

                <div className='col-12 col-md-3 col-lg-1'>
                  <label className='mb-2 d-block'>Depo</label>
                  <IPSelectBox
                    placeholder='Seçiniz'
                    options={[
                      { value: '1801', label: '1801' },
                      { value: '1802', label: '1802' },
                      { value: '1920', label: '1920' },
                    ]}
                    isDisabled={isEditLocked}
                    isClearable={false}
                    isSearchable={false}
                    value={{ value: selectedWarehouse, label: selectedWarehouse }}
                    onChangeSingle={(option: ISelectOption) =>
                      setSelectedWarehouse(option.value as string)
                    }
                  />
                </div>

                <div className='col-12 col-md-5 col-lg-3'>
                  <label className='mb-2 d-block'>Referans No</label>
                  <IPInput
                    type='text'
                    value={headerInfo.ReferenceNumber || ''}
                    disabled={true}
                    style={{ cursor: 'not-allowed' }}
                    onChange={e => handleHeaderChange('ReferenceNumber', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {selectedShippingType === '04' && (
              <div className='row mt-2'>
                <div className='col-12'>
                  <div
                    style={{
                      background: '#fff5f5',
                      border: '1px solid #f5c2c7',
                      color: '#b02a37',
                      padding: '10px 12px',
                      borderRadius: 6,
                    }}
                  >
                    *TEKLOS Depo Adresi: Cumhuriyet Mahallesi, Yahyakaptan Caddesi No:10A D:2
                    41420 Çayırova/Kocoeli
                  </div>
                </div>
              </div>
            )}

            <div className='card mb-4'>
              <div className='card-header'>Adres / Fatura Bilgileri</div>
              <div className='card-body'>
                <div className='row'>
                  <div className='col-lg-3 mb-3'>
                    <label>Firma Adı</label>
                    <IPInput
                      type='text'
                      value={headerInfo.CompanyName || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('CompanyName', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Bayi Kodu</label>
                    <IPInput
                      type='text'
                      value={headerInfo.DealerCode?.replace('0000', '') || ''}
                      disabled={true}
                      style={{ cursor: 'not-allowed' }}
                      onChange={e => handleHeaderChange('DealerCode', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Bayi Adı</label>
                    <IPInput
                      type='text'
                      value={headerInfo.DealerName ?? ''}
                      disabled={true}
                      style={{ cursor: 'not-allowed' }}
                      onChange={e => handleHeaderChange('DealerName', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-4 mb-3'>
                    <label>Mail</label>
                    <IPInput
                      type='text'
                      value={headerInfo.Mail || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('Mail', e.target.value)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-lg-2 mb-3'>
                    <label>Vergi Dairesi</label>
                    <IPInput
                      type='text'
                      value={headerInfo.TaxOffice || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('TaxOffice', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Vergi No</label>
                    <IPInput
                      type='text'
                      value={headerInfo.TaxNumber || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('TaxNumber', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Telefon</label>
                    <IPInput
                      type='text'
                      value={headerInfo.PhoneNumber || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('PhoneNumber', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Teslim Alan Adı</label>
                    <IPInput
                      type='text'
                      value={headerInfo.TakerName || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('TakerName', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Teslim Alan Soyadı</label>
                    <IPInput
                      type='text'
                      value={headerInfo.TakerSurname || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('TakerSurname', e.target.value)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-lg-2 mb-3'>
                    <label>İl</label>
                    <IPInput
                      type='text'
                      value={headerInfo.City || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('City', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>İlçe</label>
                    <IPInput
                      type='text'
                      value={headerInfo.District || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('District', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-8 mb-3'>
                    <label>Adres</label>
                    <IPInput
                      type='text'
                      value={headerInfo.Address || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('Address', e.target.value)}
                    />
                  </div>
                </div>

                <br />

                <div className='row'>
                  <div className='col-lg-3 mb-3'>
                    <label>Sipariş Tipi</label>
                    <IPInput
                      type='text'
                      value={headerInfo.Type || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('Type', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Firma Tipi</label>
                    <IPInput
                      type='text'
                      value={headerInfo.CompanyType || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('CompanyType', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Satış Yöneticisi</label>
                    <IPInput
                      type='text'
                      value={headerInfo.Manager || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('Manager', e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Satış Yöneticisi Mail</label>
                    <IPInput
                      type='text'
                      value={headerInfo.ManagerMail || ''}
                      disabled={isEditLocked}
                      onChange={e => handleHeaderChange('ManagerMail', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-12'>
                <Table columns={tableColumns} data={tableData} />
              </div>
            </div>

            <div className='card mt-3'>
              <div className='card-header'>Yeni Ürün Ekle</div>
              <div className='card-body'>
                <div className='row align-items-end'>
                  <div className='col-md-4 mb-2'>
                    <label>Ürün Kodu</label>
                    <IPInput
                      type='text'
                      value={newProductCode}
                      disabled={false}
                      onChange={e => setNewProductCode(e.target.value)}
                    />
                  </div>
                  <div className='col-md-3 mb-2'>
                    <label>Adet</label>
                    <IPInput
                      type='number'
                      min={1}
                      value={newProductCount}
                      disabled={false}
                      onChange={e => setNewProductCount(e.target.value)}
                    />
                  </div>
                  <div className='col-md-3 mb-2 d-flex'>
                    <div className='align-self-end'>
                      <IPButton text='Ürün Ekle' onClick={handleAddProduct} disabled={false} />
                    </div>
                  </div>
                </div>

                {isEditLocked && (
                  <div className='mt-2' style={{ fontSize: 12, color: '#6c757d' }}>
                    Tüm ürünlerin sipariş numarası mevcut olduğu için düzenleme kilitli. Sadece yeni
                    ürün ekleyebilirsiniz.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Sipariş No Detay Modal */}
      <Modal
        show={showOrderNoModal}
        onClose={() => setShowOrderNoModal(false)}
        hideConfirm={true}
        maxWidth={1300}
      >
        <div className='modal-content' style={{ padding: 16 }}>
          <h4 style={{ margin: 0 }}>
            Sipariş No: <span style={{ color: '#0d6efd' }}>{selectedOrderNo}</span>
          </h4>

          <hr />

          <div className='row' style={{ marginBottom: 12 }}>
            <div className='col-md-3 mb-2'>
              <b>Durum:</b> {modalOrderHeader.statusText || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Sipariş Tarihi:</b> {modalOrderHeader.orderDate || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Ödeme:</b> {modalOrderHeader.paymentText || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Sevk:</b> {modalOrderHeader.shipmentInfo || '-'}
            </div>
          </div>

          <div className='table-responsive'>
            <table className='table table-sm table-hover'>
              <thead>
                <tr>
                  <th>Ürün Kodu</th>
                  <th>Açıklama</th>
                  <th style={{ textAlign: 'right' }}>Adet</th>
                  <th style={{ textAlign: 'right' }}>Birim</th>
                  <th style={{ textAlign: 'right' }}>Toplam (Net)</th>
                  <th style={{ textAlign: 'right' }}>KDV</th>
                </tr>
              </thead>
              <tbody>
                {orderLinesForModal.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: '#6c757d' }}>
                      Detay bulunamadı.
                    </td>
                  </tr>
                ) : (
                  orderLinesForModal.map((l, idx) => {
                    const unit = formatMoney2(l.BIRIM_TUTAR)
                    const net = formatMoney2(l.TOPLAM_TUTAR)
                    const kdv = l.KDV != null ? formatMoney2(l.KDV) : null
                    return (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{l.URUN_KODU}</td>
                        <td>{l.URUN_ACIKLAMA}</td>
                        <td style={{ textAlign: 'right' }}>{l.TOPLAM_ADET}</td>
                        <td style={{ textAlign: 'right' }}>
                          {unit != null ? `${unit} ${l.PARA}` : `-`}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {net != null ? `${net} ${l.PARA}` : `-`}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {kdv != null ? `${kdv} ${l.PARA}` : '-'}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>

              {orderLinesForModal.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Toplam (Net):</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.totalNet.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Genel KDV:</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.totalKdv.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Genel Toplam:</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.grandTotal.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </Modal>

      {/* ✅ MEVCUT SİPARİŞ SONUCU MODAL (dokunulmadı) */}
      <Modal
        show={state.showOrderDetails}
        onClose={() => dispatchState({ showOrderDetails: false })}
        hideConfirm={true}
        maxWidth={1200}
      >
        <div id='order' className='modal-content'>
          <div className='form-row' style={{ justifyContent: 'center', position: 'relative' }}>
            <div className='form-group'>
              <i
                className='icon-check'
                style={{ color: 'green', fontSize: '50px', marginLeft: '160px' }}
              ></i>
              <h4>Siparişiniz Başarıyla Kaydolmuştur!</h4>
            </div>
          </div>

          {state.errorMessage &&
            state.errorMessage.length > 0 &&
            state.errorMessage.map((error, index) => (
              <div key={index}>
                <h3 style={{ color: 'red' }}>{error}</h3> <br />{' '}
              </div>
            ))}

          <p
            style={{
              marginTop: '10px',
              fontSize: '17px',
              color: 'black',
              fontWeight: '600',
              marginLeft: '20px',
            }}
          >
            SİPARİŞ SEVK ADRES BİLGİLERİ
          </p>

          <div style={{ backgroundColor: 'white', padding: '20px' }}>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Firma Adı</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.DealerName}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Sipariş No</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.OrderNumber}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>İl/İlçe</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.City + '/' + state.orderDetails.District}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Adres</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.Address}
            </div>
          </div>

          <p
            style={{
              marginTop: '10px',
              fontSize: '17px',
              color: 'black',
              fontWeight: '600',
              marginLeft: '20px',
            }}
          >
            SİPARİŞ DETAYI
          </p>

          {state.orderDetails.OrderReturnCompanyList.map((order, i) => (
            <div key={i}>
              <div style={{ backgroundColor: 'white', padding: '20px' }}>
                <div className='row'>
                  <div className='col-12 col-sm-6 col-md-4'>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Sipariş No</b>
                      <span className='mr-2'>:</span>
                      {order.OrderNumber}
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Ödeme Şekli</b>
                      <span className='mr-2'>:</span>
                      {order.PaymentMethod}
                    </div>
                  </div>

                  <div className='col-12 col-sm-6 col-md-4'>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Kur Tipi</b>
                      <span className='mr-2'>:</span>
                      {order.CurrencyType}
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Tarih</b>
                      <span className='mr-2'>:</span>
                      {state.orderDetails.CreatedDate
                        ? new Date(state.orderDetails.CreatedDate).toLocaleDateString('tr-TR')
                        : '-'}
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Sevk Tarihi</b>
                      <span className='mr-2'>:</span>
                      {state.orderDetails.ShipmentDate
                        ? new Date(state.orderDetails.ShipmentDate).toLocaleDateString('tr-TR')
                        : '-'}
                    </div>
                  </div>

                  <div className='col-12 col-sm-6 col-md-4'>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Kur</b>
                      <span className='mr-2'>:</span>
                      {order.CurrencyValue}
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>MT</b>
                      <span className='mr-2'>:</span>
                      {order.MT}
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                      <b style={{ width: '100px' }}>Sevk Biçimi</b>
                      <span className='mr-2'>:</span>{' '}
                      {state.orderDetails.ShipmentType}
                    </div>
                  </div>
                </div>
              </div>

              <div className='table-responsive'>
                <table className='table table-borderless table-hover m-t-5'>
                  <thead>
                    <tr className='EInvoicetableDescription'>
                      <td scope='col'>Depo Kodu</td>
                      <td scope='col' style={{ whiteSpace: 'nowrap' }} width={111}>
                        Ürün Kodu
                      </td>
                      <td scope='col' width={350} style={{ textAlignLast: 'center' }}>
                        Açıklama
                      </td>
                      <td scope='col'>Adet</td>
                      <td scope='col'>Birim Tutar(₺)</td>
                      <td scope='col'>
                        Birim Tutar(
                        {order.Items[0].Currency === 0 ? '₺' : order.Items[0].Currency === 1 ? '$' : '€'}
                        )
                      </td>
                      <td scope='col'>Toplam Tutar (₺)</td>
                      <td scope='col'>
                        Toplam Tutar(
                        {order.Items[0].Currency === 0 ? '₺' : order.Items[0].Currency === 1 ? '$' : '€'}
                        )
                      </td>
                    </tr>
                  </thead>

                  {order.Items.map((item, index) => (
                    <tbody key={index} style={{ borderBottom: '1px solid lightgray' }}>
                      <td>{item.Warehouse}</td>
                      <td>{item.ProductCode}</td>
                      <td>{item.ProductName}</td>
                      <td style={{ textAlign: 'center' }}>{item.Quantity}</td>
                      <td style={{ textAlign: 'right' }}>{item.Price1}</td>
                      <td style={{ textAlign: 'right' }}>{item.Price2}</td>
                      <td style={{ textAlign: 'right' }}>{item.TotalPrice1}</td>
                      <td style={{ textAlign: 'right' }}>{item.TotalPrice2}</td>
                    </tbody>
                  ))}

                  <tbody>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      <b>Toplam:</b>
                    </td>
                    <td style={{ textAlign: 'left' }}>{order.TotalPrice}</td>
                  </tbody>

                  <tbody>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      <b>Kargo:</b>
                    </td>
                    <td style={{ textAlign: 'left' }}>{order.Cargo}</td>
                  </tbody>

                  <tbody>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      <b>KDV:</b>
                    </td>
                    <td style={{ textAlign: 'left' }}>{order.TotalKdv}</td>
                  </tbody>

                  <tbody>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      <b>Genel Toplam:</b>
                    </td>
                    <td style={{ textAlign: 'left' }}>{order.Total}</td>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default componentWithContext(EditPreOrder)
